import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { logOut } from '../../auth';
import CommunityPicker from './CommunityPicker';
import { useCommunity } from '../../hooks/useCommunity';
import { apiPost } from '../../api/api';

interface AppNavProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

const TAB_ICONS: Record<string, React.ReactNode> = {
  Groups: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M21 21v-2a4 4 0 0 0-3-3.85" />
    </svg>
  ),
  Services: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.77 3.77z" />
    </svg>
  ),
  Events: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Marketplace: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
};

const NAV_TABS = [
  { label: 'Groups', path: '/groups' },
  { label: 'Services', path: '/services' },
  { label: 'Events', path: '/events' },
  { label: 'Marketplace', path: '/marketplace' },
];

const CREATE_ITEMS = [
  { label: 'Create Group', path: '/groups/create', icon: <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><circle cx="9" cy="7" r="3"/><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2M16 11h6M19 8v6"/></svg> },
  { label: 'Create Event', path: '/events/create', icon: <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg> },
  { label: 'Post a Service', path: '/services/create', icon: <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.77 3.77z"/></svg> },
  { label: 'Post a Listing', path: '/marketplace/create', icon: <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path strokeLinecap="round" strokeLinejoin="round" d="M16 10a4 4 0 01-8 0"/><line x1="12" y1="13" x2="12" y2="17"/><line x1="10" y1="15" x2="14" y2="15"/></svg> },
];

export default function AppNav({ search, onSearchChange, searchPlaceholder }: AppNavProps) {
  const { authUser, userProfile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { communityInfo, saveCommunity } = useCommunity();
  const [signingOut, setSigningOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCommunityPicker, setShowCommunityPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    setSigningOut(true);
    try {
      await logOut();
      navigate('/groups');
    } catch (e) {
      console.error(e);
    } finally {
      setSigningOut(false);
    }
  };

  const displayName = userProfile?.name || userProfile?.email || authUser?.username || '';
  const userInitial = userProfile?.initial || displayName.charAt(0).toUpperCase() || '?';
  const showSearch = onSearchChange !== undefined;
  const isHome = location.pathname === '/';

  return (
    <>
      {showCommunityPicker && (
        <CommunityPicker
          initialNationality={communityInfo?.nationality}
          initialCity={communityInfo?.city}
          onConfirm={async (nationality, city) => {
            saveCommunity(nationality, city);
            if (authUser) {
              try {
                await apiPost('/api_save_profile', { name: userProfile?.name || '', nationality, city }, true);
              } catch {}
            }
            setShowCommunityPicker(false);
            window.location.reload();
          }}
          onDismiss={() => setShowCommunityPicker(false)}
        />
      )}
      <nav
        style={{
          backgroundColor: isHome ? '#4f46e5' : '#ffffff',
          borderBottom: isHome ? 'none' : '1px solid #eef0f6',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          boxShadow: isHome ? 'none' : '0 1px 8px rgba(0,0,0,0.06)',
          overflow: 'visible',
        }}
      >
        {/* Row 1: Logo + center (tabs or search) + auth */}
        <div
          style={{
            width: '100%',
            padding: '0 2rem',
            height: scrolled ? '60px' : '72px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            boxSizing: 'border-box',
            transition: 'height 0.3s ease',
          }}
        >
          {/* Logo — always visible */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <img src="/sahayy-icon-nobg.png" alt="Sahayy" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
            <span style={{ color: isHome ? '#ffffff' : '#FF385C', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.04em' }}>
              Sahayy
            </span>
          </Link>

          {/* Center: tabs when at top, search bar when scrolled */}
          {!isMobile && (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
              {/* Tabs — visible at top */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  position: scrolled && showSearch ? 'absolute' : 'relative',
                  opacity: scrolled && showSearch ? 0 : 1,
                  pointerEvents: scrolled && showSearch ? 'none' : 'auto',
                  transition: 'opacity 0.2s ease',
                }}
              >
                {NAV_TABS.map((tab) => {
                  const isActive = location.pathname.startsWith(tab.path);
                  return (
                    <button
                      key={tab.label}
                      onClick={() => navigate(tab.path)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: isHome ? (isActive ? '#ffffff' : 'rgba(255,255,255,0.75)') : (isActive ? '#222' : '#717171'),
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '0.875rem',
                        padding: '0 1.25rem',
                        height: '72px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.15s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '6px' }}>
                        {TAB_ICONS[tab.label]}
                        <span>{tab.label}</span>
                      </div>
                      <div style={{ height: '2px', width: '100%', backgroundColor: isActive ? (isHome ? '#ffffff' : '#222') : 'transparent', borderRadius: '1px' }} />
                    </button>
                  );
                })}
              </div>

              {/* Search bar — slides up when scrolled */}
              {showSearch && (
                <div
                  style={{
                    position: scrolled ? 'relative' : 'absolute',
                    opacity: scrolled ? 1 : 0,
                    pointerEvents: scrolled ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease',
                    width: '100%',
                    maxWidth: '500px',
                  }}
                >
                  <span style={{
                    position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
                    color: '#8896ab', fontSize: '1rem', pointerEvents: 'none',
                  }}>🔍</span>
                  <input
                    type="text"
                    placeholder={searchPlaceholder || 'Search...'}
                    value={search || ''}
                    onChange={(e) => onSearchChange!(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.55rem 2.75rem 0.55rem 2.5rem',
                      backgroundColor: '#f7f8fc',
                      border: '1.5px solid #e2e6f0',
                      borderRadius: '999px',
                      color: '#1a1f36',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#FF385C'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(255,56,92,0.12)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e6f0'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  <a
                    href="https://chat.sahayy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ask Commy"
                    style={{
                      position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                      width: '26px', height: '26px', borderRadius: '50%',
                      backgroundColor: '#FF385C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.8rem', textDecoration: 'none', flexShrink: 0,
                    }}
                  >✨</a>
                </div>
              )}
            </div>
          )}

          {/* Right: profile/auth */}
          <div
            ref={menuRef}
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, position: 'relative' }}
          >
            {loading ? (
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#f0f2f7' }} />
            ) : (
              <>
                {/* Profile circle + hamburger pill */}
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'none',
                    border: isHome ? '1px solid rgba(255,255,255,0.35)' : '1px solid #dde1ea',
                    borderRadius: '999px',
                    padding: '0.3rem 0.4rem 0.3rem 0.7rem',
                    cursor: 'pointer',
                    boxShadow: menuOpen ? '0 2px 12px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.15s',
                  }}
                  aria-label="Menu"
                >
                  {/* Hamburger lines */}
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                    <rect y="0" width="16" height="2" rx="1" fill={isHome ? '#ffffff' : '#4a5568'} />
                    <rect y="5" width="16" height="2" rx="1" fill={isHome ? '#ffffff' : '#4a5568'} />
                    <rect y="10" width="16" height="2" rx="1" fill={isHome ? '#ffffff' : '#4a5568'} />
                  </svg>

                  {/* Avatar circle */}
                  {authUser ? (
                    userProfile?.picture ? (
                      <img
                        src={userProfile.picture}
                        alt={displayName}
                        referrerPolicy="no-referrer"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: '#FF385C',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {userInitial}
                      </div>
                    )
                  ) : (
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#6b7a99',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    </div>
                  )}
                </button>

                {/* Dropdown */}
                {menuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 0.5rem)',
                      right: 0,
                      backgroundColor: '#ffffff',
                      border: '1px solid #eef0f6',
                      borderRadius: '14px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      minWidth: '200px',
                      zIndex: 9999,
                      overflow: 'hidden',
                    }}
                  >
                    {authUser ? (
                      <>
                        <div
                          style={{
                            padding: '0.875rem 1rem',
                            borderBottom: '1px solid #f0f2f7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                          }}
                        >
                          {/* Mini avatar */}
                          {userProfile?.picture ? (
                            <img
                              src={userProfile.picture}
                              alt={displayName}
                              referrerPolicy="no-referrer"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{
                              width: '36px', height: '36px', borderRadius: '50%',
                              backgroundColor: '#FF385C', color: '#fff',
                              fontWeight: 700, fontSize: '0.9rem',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              {userInitial}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1a1f36' }}>
                              {userProfile?.name || userInitial}
                            </div>
                            {userProfile?.email && (
                              <div style={{ fontSize: '0.75rem', color: '#8896ab', marginTop: '1px' }}>
                                {userProfile.email}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => { setMenuOpen(false); setShowCommunityPicker(true); }}
                          style={dropdownItemStyle}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>
                          {communityInfo ? `${communityInfo.nationality} · ${communityInfo.city}` : 'Select Community'}
                        </button>
                        <button
                          onClick={() => { navigate('/settings'); setMenuOpen(false); }}
                          style={dropdownItemStyle}
                        >
                          ⚙️ Settings
                        </button>
                        {isMobile && (
                          <>
                            <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              Explore
                            </div>
                            {NAV_TABS.map((tab) => {
                              const isActive = location.pathname.startsWith(tab.path);
                              return (
                                <button
                                  key={tab.label}
                                  onClick={() => { navigate(tab.path); setMenuOpen(false); }}
                                  style={{ ...dropdownItemStyle, color: isActive ? '#FF385C' : '#1a1f36', fontWeight: isActive ? 600 : 400 }}
                                >
                                  <span style={{ color: isActive ? '#FF385C' : '#8896ab', flexShrink: 0, display: 'flex' }}>{TAB_ICONS[tab.label]}</span>
                                  {tab.label}
                                </button>
                              );
                            })}
                          </>
                        )}
                        <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Create
                        </div>
                        {CREATE_ITEMS.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => { navigate(item.path); setMenuOpen(false); }}
                            style={dropdownItemStyle}
                          >
                            {item.icon} {item.label}
                          </button>
                        ))}
                        <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Support
                        </div>
                        <a
                          href="https://wa.me/918888084629?text=Hi%21%20I%20need%20some%20help%20with%20Sahayy%20%F0%9F%99%8F"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={dropdownItemStyle}
                          onClick={() => setMenuOpen(false)}
                        >
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="none" style={{flexShrink:0}}><path fill="#25d366" d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.51L4 29l7.697-1.808A12.94 12.94 0 0016 28c6.627 0 12-5.373 12-12S22.627 3 16 3z"/></svg>
                          Chat with us
                        </a>
                        <a
                          href="https://sahayy.com/download"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={dropdownItemStyle}
                          onClick={() => setMenuOpen(false)}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" /></svg> Download App
                        </a>
                        <button
                          onClick={handleSignOut}
                          disabled={signingOut}
                          style={{ ...dropdownItemStyle, color: '#c53030', borderTop: '1px solid #f0f2f7' }}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7M13 21H5a2 2 0 01-2-2V5a2 2 0 012-2h8" /></svg>
                          {signingOut ? 'Signing out...' : 'Sign Out'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { navigate('/login'); setMenuOpen(false); }}
                          style={{ ...dropdownItemStyle, fontWeight: 700 }}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" /></svg>
                          Sign In
                        </button>
                        <button
                          onClick={() => { navigate('/login'); setMenuOpen(false); }}
                          style={dropdownItemStyle}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2M16 11h6M19 8v6" /></svg>
                          Sign Up
                        </button>
                        <button
                          onClick={() => { setMenuOpen(false); setShowCommunityPicker(true); }}
                          style={dropdownItemStyle}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>
                          {communityInfo ? `${communityInfo.nationality} · ${communityInfo.city}` : 'Select Community'}
                        </button>
                        {isMobile && (
                          <>
                            <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              Explore
                            </div>
                            {NAV_TABS.map((tab) => {
                              const isActive = location.pathname.startsWith(tab.path);
                              return (
                                <button
                                  key={tab.label}
                                  onClick={() => { navigate(tab.path); setMenuOpen(false); }}
                                  style={{ ...dropdownItemStyle, color: isActive ? '#FF385C' : '#1a1f36', fontWeight: isActive ? 600 : 400 }}
                                >
                                  <span style={{ color: isActive ? '#FF385C' : '#8896ab', flexShrink: 0, display: 'flex' }}>{TAB_ICONS[tab.label]}</span>
                                  {tab.label}
                                </button>
                              );
                            })}
                          </>
                        )}
                        <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Create
                        </div>
                        {CREATE_ITEMS.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => { navigate(item.path); setMenuOpen(false); }}
                            style={dropdownItemStyle}
                          >
                            {item.icon} {item.label}
                          </button>
                        ))}
                        <div style={{ borderTop: '1px solid #f0f2f7', padding: '0.25rem 1rem 0.1rem', fontSize: '0.7rem', fontWeight: 700, color: '#8896ab', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Support
                        </div>
                        <a
                          href="https://wa.me/918888084629?text=Hi%21%20I%20need%20some%20help%20with%20Sahayy%20%F0%9F%99%8F"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={dropdownItemStyle}
                          onClick={() => setMenuOpen(false)}
                        >
                          <svg width="16" height="16" viewBox="0 0 32 32" fill="none" style={{flexShrink:0}}><path fill="#25d366" d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.51L4 29l7.697-1.808A12.94 12.94 0 0016 28c6.627 0 12-5.373 12-12S22.627 3 16 3z"/></svg>
                          Chat with us
                        </a>
                        <a
                          href="https://sahayy.com/download"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={dropdownItemStyle}
                          onClick={() => setMenuOpen(false)}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{flexShrink:0}}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" /></svg> Download App
                        </a>
                      </>
                    )}

                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Row 2: Search bar — collapses on scroll (moves into row 1 center) */}
        {showSearch && (
          <div
            style={{
              width: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              overflow: 'hidden',
              maxHeight: scrolled ? '0' : '64px',
              opacity: scrolled ? 0 : 1,
              padding: scrolled ? '0 2rem' : '0 2rem 0.75rem',
              transition: 'max-height 0.3s ease, opacity 0.2s ease, padding 0.3s ease',
              pointerEvents: scrolled ? 'none' : 'auto',
            }}
          >
            <div
              style={{
                maxWidth: '680px',
                margin: '0 auto',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#8896ab',
                  fontSize: '1rem',
                  pointerEvents: 'none',
                }}
              >
                🔍
              </span>
              <input
                type="text"
                placeholder={searchPlaceholder || 'Search...'}
                value={search || ''}
                onChange={(e) => onSearchChange!(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.7rem 3rem 0.7rem 2.75rem',
                  backgroundColor: '#f7f8fc',
                  border: '1.5px solid #e2e6f0',
                  borderRadius: '999px',
                  color: '#1a1f36',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#FF385C';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(255,56,92,0.12)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e2e6f0';
                  e.currentTarget.style.backgroundColor = '#f7f8fc';
                  e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.05)';
                }}
              />
              <a
                href="https://chat.sahayy.com"
                target="_blank"
                rel="noopener noreferrer"
                title="Ask Commy"
                style={{
                  position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                  width: '30px', height: '30px', borderRadius: '50%',
                  backgroundColor: '#FF385C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.9rem', textDecoration: 'none', flexShrink: 0,
                }}
              >✨</a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

const dropdownItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: '#1a1f36',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.1s',
};
